import { useState, useEffect, useCallback } from 'react';
import { userService } from '../_services';

/**
 * Custom hook for fetching report data
 * Handles loading, error states, and data fetching
 * 
 * @param {string} reportType - Type of report to fetch
 * @param {Object} filters - Filter parameters
 * @param {boolean} autoFetch - Whether to fetch on mount (default: true)
 * @returns {Object} { data, loading, error, refetch, setData }
 */
export function useReportData(reportType, filters = {}, autoFetch = true) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(autoFetch);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!reportType) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
            const userId = usermetaData.split(':')[0];
            const userName = usermetaData.split(':')[1];

            let apiData = null;

            // Route to appropriate API based on report type
            switch (reportType) {
                case 'binList':
                    apiData = await userService.GetBinList(userId, userName);
                    break;

                case 'userTransactionsByBin':
                    if (filters.bin && filters.startDate && filters.endDate) {
                        apiData = await userService.GetDownloadTransactionbyBin(
                            userId,
                            filters.startDate,
                            filters.endDate,
                            filters.bin
                        );
                    }
                    break;

                case 'groupsNoTransactions':
                    if (filters.startDate && filters.endDate) {
                        apiData = await userService.GetDownloadGroups(
                            userId,
                            filters.startDate,
                            filters.endDate
                        );
                    }
                    break;

                case 'usersTransactions':
                    if (filters.startDate && filters.endDate) {
                        apiData = await userService.GetDownloadUsersTransactions(
                            userId,
                            filters.startDate,
                            filters.endDate
                        );
                    }
                    break;

                case 'parentReferral':
                    if (filters.startDate && filters.endDate) {
                        apiData = await userService.GetDownloadParentReferrals(
                            userId,
                            filters.startDate,
                            filters.endDate
                        );
                    }
                    break;

                case 'userTierMonths':
                    const userrole = window.atob(localStorage.getItem('_Host_Pro_type'));
                    apiData = await userService.GetListOfMonth(userId, userrole);
                    break;

                case 'userTierReport':
                    if (filters.month) {
                        apiData = await userService.GetUsersTiersbyMonth(userId, filters.month);
                    }
                    break;

                case 'minimumPayoutMonths':
                    const role = window.atob(localStorage.getItem('_Host_Pro_type'));
                    apiData = await userService.GetListOfMonthPayout(userId, role);
                    break;

                case 'minimumPayoutReport':
                    if (filters.month) {
                        apiData = await userService.GetMinimumPayoutReport(userId, filters.month);
                    }
                    break;

                case 'commissionPayoutMonths':
                    apiData = await userService.GetListOfMonthPayout();
                    break;

                case 'commissionPayoutReport':
                    if (filters.month) {
                        apiData = await userService.GetPayoutReportData(userId, userName, filters.month);
                    }
                    break;

                case 'transaction6m':
                    apiData = await userService.GetTransaction6m();
                    break;

                default:
                    throw new Error(`Unknown report type: ${reportType}`);
            }

            setData(apiData);
            setError(null);
        } catch (err) {
            console.error(`Error fetching ${reportType} report:`, err);
            setError(err.message || 'Failed to fetch report data');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [reportType, JSON.stringify(filters)]);

    useEffect(() => {
        if (autoFetch) {
            fetchData();
        }
    }, [fetchData, autoFetch]);

    return {
        data,
        loading,
        error,
        refetch: fetchData,
        setData // Allow manual data updates
    };
}

export default useReportData;
